import {
  MEASURES as ORIGINAL_DELIVERY_METRICS_MEASURES,
  MEASURE_LABELS as ORIGINAL_DELIVERY_METRICS_MEASURE_LABELS,
  Measure,
  ALL_CHANNELS,
} from 'cio/utils/metrics/common';
import {
  METRICS as ORIGINAL_JOURNEY_METRICS_MEASURES,
  MEASURE_LABELS as ORIGINAL_JOURNEY_METRICS_MEASURE_LABELS,
  JourneyMetricsMeasure,
} from 'cio/utils/journey-metrics';
import { CampaignType } from 'cio/models/campaign';

export const ALL_DATA_TYPES = ['campaign', 'newsletter', 'broadcast', 'transactional'] as const;

export type AnalysisDataType = (typeof ALL_DATA_TYPES)[number];
export type AnalysisChannel = (typeof ALL_CHANNELS)[number];

// These are defined manually rather than extended from other arrays, as we want
// to maintain the measures in this order
export const MEASURES = [
  ...[
    'sent',
    'delivered',
    'opened',
    'clicked',
    'clicked_to_opened',
    'converted',
    'unsubscribed',
    'topic_unsubscribed',
    'spammed',
    'failed',
    'bounced',
    'suppressed',
  ].map((measure) => `delivery_metrics.${measure}`),
  ...['activated', 'in_workflow', 'converted', 'exited_early', 'finished'].map(
    (measure) => `journey_metrics.${measure}`,
  ),
];

// In the Analysis "total" calculation, for "converted" metrics, we use
// "converted_denominator" instead of the usual denominator, if present.
// This is because for analysis totals, we use custom denominator values, since
// for conversion calculations we only take into account
// campaigns/newsletters/messages that actually
// have conversion goals set.
// If they can't convert, they're not counted, and are excluded from the
// denominator totals (whereas the regular "sent"/"delivered" values include
// ALL metrics).
export const CUSTOM_CONVERTED_DENOMINATOR = 'converted_denominator';

export const DELIVERY_METRICS_MEASURE_LABELS = {
  ...ORIGINAL_DELIVERY_METRICS_MEASURE_LABELS,
};

export const DELIVERY_METRICS_MEASURES = [...ORIGINAL_DELIVERY_METRICS_MEASURES];

export const JOURNEY_METRICS_MEASURES = [...ORIGINAL_JOURNEY_METRICS_MEASURES, 'in_workflow'];

export const JOURNEY_METRICS_MEASURE_LABELS = {
  ...ORIGINAL_JOURNEY_METRICS_MEASURE_LABELS,
  converted: 'Journeys converted',
};

export const MEASURE_LABELS = {
  ...(Object.keys(DELIVERY_METRICS_MEASURE_LABELS) as Measure[]).reduce<Record<string, string>>((acc, measure) => {
    acc[`delivery_metrics.${measure}`] = DELIVERY_METRICS_MEASURE_LABELS[measure];
    return acc;
  }, {}),
  ...(Object.keys(JOURNEY_METRICS_MEASURE_LABELS) as JourneyMetricsMeasure[]).reduce<Record<string, string>>(
    (acc, measure) => {
      acc[`journey_metrics.${measure}`] = JOURNEY_METRICS_MEASURE_LABELS[measure];
      return acc;
    },
    {},
  ),
};

export type ProcessedMetricItem = {
  id: string;
  _id: string;
  name: string;
  type: CampaignType | 'newsletter' | 'transactional_message';
  icon: string;
  data: {
    per_channel_metrics: Record<string, Record<string, number[] | number>>;
    delivery_metrics: Record<string, number[] | number>;
    journey_metrics: Record<string, number[] | number>;
    per_subscription_topic_metrics: Record<string, number>;
  };
  channel?: null | string; // if the item is a newsletter / transactional_message, this will tell us
  // what the newsletter's/transactional_message's channel is
  conversion?: boolean;
};

export type SimpleCampaignNewsletter = {
  _id: string;
  id: string;
  name: string;
  type: CampaignType | 'newsletter' | 'transactional_message';
  icon: string;
};

export type MetricItem = {
  label: string;
  value: string;
  displayOnly: boolean;
  items: Record<string, string>[];
};
